import {
    type FC,
    useMemo,
    useState,
    useEffect,
    useContext,
    useCallback,
    createContext,
    type ReactNode,
} from 'react';
import {
    addLoginHandlers,
    sendChatMessageOnPageRefetch,
} from 'dibs-buyer-layout/exports/loginHandlers';
import { useDispatch, useSelector } from 'react-redux';
import {
    getUserZipCode,
    getUserSessionCountryCode,
} from 'dibs-regional-info/exports/regionalInfoHelpers';
import { useDefaultSkuId } from '../../hooks/useDefaultSkuId';
import { useFragment, graphql, useRelayEnvironment } from 'react-relay';
import { useServerVarsContext } from '../../../global/ServerVarsContext/ServerVarsContext';
import { setPdpUserRegionalInfo } from '../../helpers/setPdpUserRegionalInfo';
import { updateRelayVariables } from '../../../actions/sharedRelayVariableActions';
import { handleLocaleUrl, GLOBAL_CLIENT_ONLY_LOCALE } from 'dibs-intl/exports/urls';
import { CUSTOMER_TYPE_TRADE } from 'dibs-buyer-layout/exports/userStorageConstants';
import {
    getBuyerId,
    getUserType,
    getConsentedBuyerId,
    getPriceBookName,
    getGuestId,
} from 'dibs-cookie-jar';

// types
import { type pdpRespMarketplacePrimaryRootQuery$variables as RelayVariables } from '../../../queries/pdpRespMarketplacePrimaryRoot';
import { type PdpRespMarketplaceLayoutPrimaryContextProvider_viewer$key as Viewer } from './__generated__/PdpRespMarketplaceLayoutPrimaryContextProvider_viewer.graphql';

type Refetch = (vars: $TSFixMe, options: { onComplete: (error: Error | null) => void }) => void;

type PdpRespMarketplaceLayoutPrimaryContextType = {
    selectedSkuId: string | null;
    onSkuChange: (skuId: string) => void;
    currency: string;
    printWithPrice: boolean;
    relayVariables: {
        variables: RelayVariables;
    };
};

type ReduxStoreType = {
    currency: {
        currency: string;
    };
    relayVariables: {
        variables: RelayVariables;
    };
    pdpPrint: {
        printWithPrice: boolean;
    };
};

const PdpRespMarketplaceLayoutPrimaryContext =
    createContext<PdpRespMarketplaceLayoutPrimaryContextType>({
        selectedSkuId: null,
        onSkuChange: () => {},
        currency: '',
        printWithPrice: false,
        relayVariables: {
            variables: {
                itemId: '',
                itemIds: [],
                afterDisplay: false,
                afterDisplayOrBuaSsr: false,
                guestId: '',
                userId: '',
                userIds: [],
                isTrade: false,
                isMultiSku: false,
                isPDPDesktopAggregationVariant: false,
                isInteractivePdpChat: false,
                personalizationId: '',
                fetchUser: false,
                similarItemsCount: 0,
            },
        },
    });

const viewerFragment = graphql`
    fragment PdpRespMarketplaceLayoutPrimaryContextProvider_viewer on Viewer {
        ...useDefaultSkuId_viewer
        itemRead(itemIds: $itemIds) {
            ... on Item {
                isMultiSku
                skus {
                    skuId
                }
                seller {
                    isAccessible
                }
            }
        }
    }
`;

export const PdpRespMarketplaceLayoutPrimaryContextProvider: FC<{
    viewer: Viewer;
    refetch: Refetch;
    children: ReactNode;
}> = ({ viewer: viewerRef, refetch, children }) => {
    const dispatch = useDispatch();
    const environment = useRelayEnvironment();
    const [localRelayVars, setLocalRelayVars] = useState<Partial<RelayVariables> | null>();
    const viewer = useFragment(viewerFragment, viewerRef);
    const { defaultSkuId } = useDefaultSkuId({ viewer });
    const [selectedSkuId, setSelectedSkuId] = useState<string | null>(null);
    const {
        pdpPrint,
        relayVariables,
        currency: currencyObject,
    } = useSelector<ReduxStoreType, ReduxStoreType>(relay => relay);
    const { isBot, pdpTopYmalSponsoredTestVariant } = useServerVarsContext();

    const currency = currencyObject?.currency;
    const printWithPrice = pdpPrint?.printWithPrice;

    const handleRefetch = useCallback(async () => {
        const userId = getBuyerId(document.cookie) || '';
        const userType = getUserType(document.cookie);
        const isTrade = userType === CUSTOMER_TYPE_TRADE;

        await setPdpUserRegionalInfo({ userId, environment });

        const updatedRelayVars = {
            userId,
            guestId: getGuestId(document.cookie) || '',
            isTrade,
            afterDisplay: true,
            skipSponsoredItems: isBot || pdpTopYmalSponsoredTestVariant !== 'variant',
            fetchUser: !!userId,
            afterDisplayOrBuaSsr: true,
            userZipCode: getUserZipCode(),
            userIds: userId ? [userId] : [],
            userCountryCode: getUserSessionCountryCode(),
            isMultiSku: !!viewer?.itemRead?.[0]?.isMultiSku,
            personalizationId: getConsentedBuyerId(document.cookie) || '',
            priceBookName: getPriceBookName(document.cookie) || 'DEFAULT',
            isInteractivePdpChat: !!viewer?.itemRead?.[0]?.seller?.isAccessible,
        };

        refetch(updatedRelayVars, {
            onComplete: error => {
                if (!error) {
                    setLocalRelayVars(updatedRelayVars);
                    sendChatMessageOnPageRefetch();
                }
            },
        });
    }, [refetch, viewer, environment, isBot, pdpTopYmalSponsoredTestVariant]);

    const onSkuChange = useCallback(
        (skuId: string) => {
            const item = viewer?.itemRead?.[0] || {};
            if (item.isMultiSku && (item.skus || []).find(sku => sku?.skuId === skuId)) {
                setSelectedSkuId(skuId);
            }
        },
        [viewer]
    );

    // set selected sku id
    useEffect(() => {
        const isMultiSku = viewer?.itemRead?.[0]?.isMultiSku;
        if (!isMultiSku) {
            return;
        }
        if (selectedSkuId === null) {
            setSelectedSkuId(defaultSkuId);
        } else {
            // update hash when sku changes
            const queryParams = window.location.search || '';
            window.history.replaceState(
                null,
                '',
                handleLocaleUrl(
                    `${window.location.pathname}${queryParams}#skuId=${selectedSkuId}`,
                    GLOBAL_CLIENT_ONLY_LOCALE
                )
            );
        }
    }, [selectedSkuId, viewer, defaultSkuId]);

    useEffect(() => {
        if (localRelayVars) {
            dispatch(updateRelayVariables(localRelayVars));
        }
    }, [localRelayVars, dispatch]);

    useEffect(() => {
        handleRefetch();
        addLoginHandlers(() => {
            handleRefetch();
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const value = useMemo(
        () => ({
            currency,
            onSkuChange,
            selectedSkuId,
            printWithPrice,
            relayVariables: {
                variables: {
                    ...relayVariables?.variables,
                },
            },
        }),
        [currency, selectedSkuId, onSkuChange, printWithPrice, relayVariables]
    );
    return (
        <PdpRespMarketplaceLayoutPrimaryContext.Provider value={value}>
            {children}
        </PdpRespMarketplaceLayoutPrimaryContext.Provider>
    );
};

export const usePdpRespMarketplaceLayoutPrimaryContext =
    (): PdpRespMarketplaceLayoutPrimaryContextType => {
        return useContext(PdpRespMarketplaceLayoutPrimaryContext);
    };
